import { getOwner } from '@ember/owner'
import type Owner from '@ember/owner'
import { lookupStore } from './index'

const commandModules: Record<string, CommandModule> = import.meta.glob('../../commands/**/*.ts', {
  eager: true,
  import: 'default'
})

// Demo mode swaps in any method a module under `demo/commands` defines, keyed by the same path as the real command.
const demoCommandModules: Record<string, CommandModule> = import.meta.glob('../../demo/commands/**/*.ts', {
  eager: true,
  import: 'default'
})

/**
 * This utility function generates a command resolver function that looks up and keys commands into a map based on their domain.
 *
 * @param {Object} commandMap - A map of command modules keyed by their domain names.
 * @returns {Function} - A function that, given a mapping of command dependencies,
 *                       resolves and initializes the appropriate commands.
 *
 * The returned function accepts a `commandDependencies` object where each key is a prefix
 * and each value is an array of strings denoting the required commands in the format:
 * `commandName:methodName`. It utilizes the provided `commandMap` to look up and cache
 * commands, ensuring that commands are registered and initialized only once per owner.
 *
 * The function leverages Ember's `getOwner` to look up services and inject them into
 * commands dynamically using a proxy. Commands can define dependencies on specific services
 * and can include wildcard (`*`) methods to include all functions within a command module.
 *
 * Throws errors in the following cases:
 * - If the `owner` is not found in the current `this` context.
 * - If a specified command or method is not registered in the `commandMap`.
 *
 * This allows for efficient, lazy loading and dependency injection of command
 * modules.
 */
export default function constructCommandResolver(createDispatch: (_context: any) => CommandDispatcher) {
  return function (modulePath: string, commandName: string): CommandFunction {
    // 'this' context is passed in from the decorator ('this' is the class instance)
    // @ts-ignore: Decorator context typing
    const owner = getOwner(this)

    if (!owner) {
      throw new Error('Unable to find the owner. Ensure you call this function with the correct `this` context.')
    }

    // proxy to lazyily lookup services, allows for syntax: { serviceName } = services
    const services = new Proxy(
      {},
      {
        get(_target, serviceName: string) {
          return owner.lookup(`service:${serviceName}`)
        }
      }
    )

    // proxy to lazyily lookup stores, allows for syntax: { storeName } = stores
    const stores = new Proxy({}, {
      get<T extends keyof StoreRegistry>(_target: any, storeName: T): StoreType<T> {
        return lookupStore(owner, storeName)
      }
    })

    let commandInstance
    let demoInstance

    try {
      const commandModule = commandModules[`../../commands/${modulePath}.ts`]
      if (!commandModule) {
        throw new Error(`Command module "${modulePath}" could not be loaded.`)
      }

      const commandArgs = {
        stores,
        services,
        // @ts-ignore: Decorator context typing
        dispatch: createDispatch?.(this),
        owner,
        // @ts-ignore: Decorator context typing
        callerContext: this
      }

      commandInstance = commandModule(commandArgs)
      demoInstance = resolveDemoInstance(owner, modulePath, commandArgs)
    } catch (e) {
      throw new Error(`utils/decorators/construct-command-resolver: ${(e as Error).message}`)
    }

    const commands: CommandFunction = {}

    if (!commands[modulePath]) {
      commands[modulePath] = {}
    }

    // a demo method, when there is one, wins over the real one
    const sourceFor = (key: string) => (typeof demoInstance?.[key] === 'function' ? demoInstance : commandInstance)

    if (commandName === '*') {
      // if wildcard, add all methods
      new Set([...getClassMethods(commandInstance), ...Object.keys(demoInstance ?? {})]).forEach((key) => {
        const source = sourceFor(key)
        commands[modulePath][key] = source[key]?.bind(source)
      })
    } else {
      const source = sourceFor(commandName)
      const command = source[commandName]

      if (typeof command !== 'function') {
        throw new Error(`"${commandName}" does not exist on command "${modulePath}".`)
      }

      commands[modulePath][commandName] = command.bind(source)
    }

    return commands
  }
}

function resolveDemoInstance(owner: Owner, modulePath: string, commandArgs: CommandArgs) {
  const demoModule = demoCommandModules[`../../demo/commands/${modulePath}.ts`]
  if (!demoModule) return undefined

  const application = owner.lookup('service:application') as Application
  return application.isDemoMode ? demoModule(commandArgs) : undefined
}

function getClassMethods(instance: Context) {
  const proto = Object.getPrototypeOf(instance)
  return Object.getOwnPropertyNames(proto).filter((prop) => prop !== 'constructor')
}
